import { useEffect, useState } from "react";
import type { ApiConflict } from "../../api/types";
import css from "./ConflictOverride.module.css";

/**
 * Саб-модал 409-конфликтов гейта бронирований (по образцу CancelReasonPrompt:
 * overlay + gradient-panel + Escape). Показывает список conflicts с маркером
 * по severity (block=красный, warning=янтарный, info=серый) и staff-кнопку
 * force-повтора с осознанным вторым кликом (armed) — native confirm запрещён
 * (DECISIONS A3). Бэк на force=true пишет audit forced_* с attention.
 *
 * Кнопка force рендерится только при наличии severity==='block' (при 409 это
 * всегда так; 409 без conflicts — точный дубль слота — сюда не попадает).
 */
export interface ConflictOverrideProps {
  conflicts: ApiConflict[];
  /** Текст danger-кнопки: «ВСЁ РАВНО СОЗДАТЬ» / «ВСЁ РАВНО ПЕРЕНЕСТИ». */
  actionLabel: string;
  onForce: () => void;
  onDismiss: () => void;
}

const SEVERITY_CSS: Record<ApiConflict["severity"], string | undefined> = {
  block: css.sevBlock,
  warning: css.sevWarning,
  info: css.sevInfo,
};

export function ConflictOverride(props: ConflictOverrideProps) {
  const { conflicts, actionLabel, onForce, onDismiss } = props;
  const [armed, setArmed] = useState(false);
  const hasBlock = conflicts.some((c) => c.severity === "block");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  return (
    <div
      className={css.overlay}
      onClick={(e) => e.target === e.currentTarget && onDismiss()}
      role="presentation"
    >
      <div
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Конфликты слота"
      >
        <div className={css.body}>
          <div className={css.title}>Слот конфликтует</div>

          <ul className={css.list}>
            {conflicts.map((c, i) => (
              <li key={`${c.type}_${i}`} className={css.item}>
                <span
                  className={`${css.marker} ${SEVERITY_CSS[c.severity] ?? css.sevInfo}`}
                  aria-hidden="true"
                />
                <span className={css.msg}>{c.msg}</span>
              </li>
            ))}
          </ul>

          <div className={css.hint}>
            {armed
              ? "Точно? Блокирующие конфликты будут продавлены."
              : "Продавить может только персонал — действие уйдёт в аудит с пометкой."}
          </div>

          <div className={css.actions}>
            {hasBlock ? (
              <button
                type="button"
                className={`${css.btn} ${css.btnForce} ${armed ? css.armed : ""}`}
                onClick={() => (armed ? onForce() : setArmed(true))}
              >
                {armed ? "ТОЧНО? ПРОДАВИТЬ" : actionLabel}
              </button>
            ) : null}
            <button type="button" className={`${css.btn} ${css.btnGhost}`} onClick={onDismiss}>
              ОТМЕНА
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
